import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import {
  CorrespondenceType,
  CommunicationThreadType as GeneratedCommunicationThreadType,
  RecipientReferenceType,
} from 'compliance-api/generated';
import dayjs, { Dayjs } from 'dayjs';
import pluralize from 'pluralize';
import { useEffect, useMemo, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import {
  CommunicationMessage,
  CommunicationRecipientDetail,
  fetchCommunicationMessages,
  useCreateCommunicationHistoryRecordMutation,
  useUpdateCommunicationRecordMutation,
} from '@/modules/admin-dashboard/queries/communications.ts';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CustomCheckbox,
  Dropzone,
  ModalContentWrapper,
  Select,
  SelectOption,
  TextInput,
} from 'ui';
import { TrashIcon } from 'ui/icons';
import { replaceQuillSizeOptionIcons, sanitizeCorrespondenceHtml } from 'ui/utils';
import 'react-quill/dist/quill.snow.css';

interface CommunicationClient {
  id: number;
  name: string;
  email?: string | null;
  correspondence_email?: string | null;
}

interface CreateCommunicationHistoryRecordModalProps extends BaseModalProps {
  client?: CommunicationClient;
  record?: CommunicationMessage | null;
}

interface ExistingAttachment {
  object_key: string;
  file_name: string;
}

const FontSizeStyle = Quill.import('attributors/style/size') as any;

FontSizeStyle.whitelist = ['10px', '14px', '18px', '24px', '32px'];
Quill.register(FontSizeStyle, true);

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'size',
  'script',
  'header',
  'list',
  'link',
];

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: '24px' }, { size: '32px' }],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
const splitCommaSeparatedValues = (value: string) =>
  value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const correspondenceTypeOptions: SelectOption<CorrespondenceType>[] = [
  { value: CorrespondenceType.Email, label: 'Sent email' },
  { value: CorrespondenceType.PhysicalMail, label: 'Physical mail' },
  { value: CorrespondenceType.PhoneCall, label: 'Phone call' },
  { value: CorrespondenceType.LiveMeeting, label: 'In-person meeting' },
];

export const CreateCommunicationHistoryRecordModal = ({
  open,
  onClose,
  client,
  record,
}: CreateCommunicationHistoryRecordModalProps) => {
  const isEditMode = !!record;
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [extraEmails, setExtraEmails] = useState('');
  const [meetingLocation, setMeetingLocation] = useState('');
  const [occurredAt, setOccurredAt] = useState<Dayjs | null>(dayjs());
  const [correspondenceType, setCorrespondenceType] = useState<CorrespondenceType>(
    CorrespondenceType.PhysicalMail,
  );
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>(client ? [client.id] : []);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([]);
  const { mutateAsync: createRecord, isLoading: isCreateLoading } =
    useCreateCommunicationHistoryRecordMutation();
  const { mutateAsync: updateRecord, isLoading: isUpdateLoading } =
    useUpdateCommunicationRecordMutation();
  const { data: currentUser } = useCurrentUserQuery();
  const isLoading = isCreateLoading || isUpdateLoading;
  const fullRecordQuery = useQuery({
    queryKey: ['communication-edit-record', record?.id],
    queryFn: async () => {
      if (!record?.id) {
        throw new Error('record id is required');
      }

      const threadId = record.communication_id || record.id;
      const threadType = (
        record.communication_id ? 'communication' : 'external'
      ) as GeneratedCommunicationThreadType;
      const response = await fetchCommunicationMessages(threadId, threadType);

      return response.data.find((item: CommunicationMessage) => item.id === record.id) || record;
    },
    enabled: open && !!record?.id && !record?.attachments,
    staleTime: 1000 * 30,
  });
  const resolvedRecord = fullRecordQuery.data || record;

  const { data: clientsData, isLoading: isClientsLoading } = useClientsListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    false,
    { cacheKey: 'communications-modal-clients' },
  );

  const { data: usersData, isLoading: isUsersLoading } = useUsersListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    { cacheKey: 'communications-modal-users' },
  );

  const clients = useMemo(
    () => (clientsData?.pages || []).flatMap(page => page.data) || [],
    [clientsData?.pages],
  );
  const users = useMemo(
    () =>
      ((usersData?.pages || []).flatMap(page => page.data) || []).filter(
        item => item.id !== currentUser?.user.id,
      ),
    [currentUser?.user.id, usersData?.pages],
  );

  useEffect(() => {
    replaceQuillSizeOptionIcons();
  }, []);

  useEffect(() => {
    if (!open) return;

    if (!record) {
      setSubject('');
      setContent('');
      setPhoneNumber('');
      setExtraEmails('');
      setMeetingLocation('');
      setOccurredAt(dayjs());
      setCorrespondenceType(CorrespondenceType.PhysicalMail);
      setSelectedUserIds([]);
      setSelectedClientIds(client ? [client.id] : []);
      setSelectedFiles([]);
      setExistingAttachments([]);
      return;
    }

    const contentInsideWrapper =
      record.content?.match(/^<div[^>]*>([\s\S]*)<\/div>$/i)?.[1]?.trim() || record.content || '';
    const liveMeetingPrefixMatch = contentInsideWrapper.match(
      /^<p[^>]*><strong>Location:<\/strong>\s*([^<]+)<\/p>([\s\S]*)$/i,
    );
    const normalizedContent = liveMeetingPrefixMatch
      ? liveMeetingPrefixMatch[2].trim()
      : contentInsideWrapper;
    const externalRecipients = (record.recipient_details || [])
      .filter(item => item.type === 'external')
      .map(item => item.email || item.label)
      .filter(Boolean) as string[];

    setSubject(record.subject || '');
    setContent(normalizedContent);
    setPhoneNumber(
      record.correspondence_type === CorrespondenceType.PhoneCall
        ? externalRecipients[0] || ''
        : '',
    );
    setExtraEmails(
      record.correspondence_type === CorrespondenceType.PhoneCall
        ? externalRecipients.slice(1).join(', ')
        : externalRecipients.join(', '),
    );
    setMeetingLocation(liveMeetingPrefixMatch?.[1]?.trim() || '');
    setOccurredAt(record.date ? dayjs(record.date) : dayjs());
    setCorrespondenceType(record.correspondence_type || CorrespondenceType.PhysicalMail);
    setSelectedUserIds(
      (record.recipient_details || [])
        .map(item => item.user_id)
        .filter((value): value is number => typeof value === 'number'),
    );
    setSelectedClientIds(
      (record.recipient_details || [])
        .map(item => item.client_id)
        .filter((value): value is number => typeof value === 'number'),
    );
    setSelectedFiles([]);
    setExistingAttachments(
      (record.attachments || []).map(item => ({
        object_key: item.object_key,
        file_name: item.file_name,
      })),
    );
  }, [client, open, record]);

  useEffect(() => {
    if (!open || !record || !resolvedRecord?.attachments) return;
    setExistingAttachments(
      resolvedRecord.attachments.map((item: ExistingAttachment) => ({
        object_key: item.object_key,
        file_name: item.file_name,
      })),
    );
  }, [open, record, resolvedRecord?.attachments]);

  const isPhoneCall = correspondenceType === CorrespondenceType.PhoneCall;
  const isLiveMeeting = correspondenceType === CorrespondenceType.LiveMeeting;

  const parsedExtraEmails = useMemo(() => splitCommaSeparatedValues(extraEmails), [extraEmails]);
  const parsedPhoneReferences = useMemo(
    () => splitCommaSeparatedValues(phoneNumber),
    [phoneNumber],
  );
  const recipientReferences = useMemo(
    () => [...parsedPhoneReferences, ...parsedExtraEmails],
    [parsedExtraEmails, parsedPhoneReferences],
  );

  const checkedAllClients = selectedClientIds.length === clients.length && clients.length > 0;
  const indeterminateClients = !!selectedClientIds.length && !checkedAllClients;
  const checkedAllUsers = selectedUserIds.length === users.length && users.length > 0;
  const indeterminateUsers = !!selectedUserIds.length && !checkedAllUsers;

  const additionalRecipientLabels = useMemo(
    () => [
      ...users
        .filter(item => selectedUserIds.includes(item.id))
        .map(
          item =>
            [item.first_name, item.last_name].filter(Boolean).join(' ') ||
            item.email ||
            `User #${item.id}`,
        ),
      ...clients
        .filter(item => selectedClientIds.includes(item.id))
        .map(item => item.name || item.email || `Client #${item.id}`),
      ...recipientReferences,
    ],
    [clients, recipientReferences, selectedClientIds, selectedUserIds, users],
  );
  const additionalRecipientDetails = useMemo<CommunicationRecipientDetail[]>(
    () => [
      ...users
        .filter(item => selectedUserIds.includes(item.id))
        .map(item => ({
          type: RecipientReferenceType.User,
          label:
            [item.first_name, item.last_name].filter(Boolean).join(' ') ||
            item.email ||
            `User #${item.id}`,
          email: item.email,
          user_id: item.id,
        })),
      ...clients
        .filter(item => selectedClientIds.includes(item.id))
        .map(item => ({
          type: RecipientReferenceType.Client,
          label: item.name || item.email || `Client #${item.id}`,
          email: item.email,
          client_id: item.id,
        })),
      ...recipientReferences.map(value => ({
        type: RecipientReferenceType.External,
        label: value,
        email: value,
      })),
    ],
    [clients, recipientReferences, selectedClientIds, selectedUserIds, users],
  );

  const handleToggleClient = (clientId: number, checked: boolean) => {
    setSelectedClientIds(current =>
      checked ? [...current, clientId] : current.filter(id => id !== clientId),
    );
  };

  const handleToggleUser = (userId: number, checked: boolean) => {
    setSelectedUserIds(current =>
      checked ? [...current, userId] : current.filter(id => id !== userId),
    );
  };

  const handleToggleAllClients = (checked: boolean) => {
    setSelectedClientIds(checked ? clients.map(item => item.id) : []);
  };

  const handleToggleAllUsers = (checked: boolean) => {
    setSelectedUserIds(checked ? users.map(item => item.id) : []);
  };

  const handleAcceptFiles = (files: File[]) => {
    setSelectedFiles(current => {
      const next = [...current];
      files.forEach(file => {
        if (next.length >= 10) return;
        const alreadyAdded = next.some(
          item =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified,
        );
        if (!alreadyAdded) {
          next.push(file);
        }
      });
      return next;
    });
  };

  const handleRemoveSelectedFile = (targetFile: File) => {
    setSelectedFiles(current =>
      current.filter(
        file =>
          !(
            file.name === targetFile.name &&
            file.size === targetFile.size &&
            file.lastModified === targetFile.lastModified
          ),
      ),
    );
  };

  const handleRemoveExistingAttachment = (objectKey: string) => {
    setExistingAttachments(current => current.filter(item => item.object_key !== objectKey));
  };

  const formattedContent = useMemo(() => {
    const sanitizedContent = sanitizeCorrespondenceHtml(content);

    return `<div style="text-align: initial">
        ${
          isLiveMeeting && meetingLocation.trim()
            ? `<p style='margin: 0'><strong>Location:</strong> ${escapeHtml(
                meetingLocation.trim(),
              )}</p>`
            : ''
        }
        ${sanitizedContent
          .replaceAll('<p>', "<p style='margin: 0'>")
          .replaceAll('<h1>', "<h1 style='margin: 0'>")
          .replaceAll('<h2>', "<h2 style='margin: 0'>")
          .replaceAll('<ul>', "<ul style='margin: 0'>")
          .replaceAll('<li>', "<li style='margin: 0'>")}
      </div>`;
  }, [content, isLiveMeeting, meetingLocation]);

  const formCopy = useMemo(() => {
    if (isPhoneCall) {
      return {
        occurredAtLabel: 'Call time',
        subjectLabel: 'Call topic',
        subjectPlaceholder: 'What was this call about?',
        contentLabel: 'Call notes',
        contentHint: 'Summarise the conversation, outcomes, and any follow-up actions.',
        extraRecipientsLabel: 'Other participant references',
        extraRecipientsPlaceholder: 'name@example.com, alternate phone number, external contact',
        submitLabel: isEditMode ? 'Save call changes' : 'Save call record',
      };
    }

    if (isLiveMeeting) {
      return {
        occurredAtLabel: 'Meeting time',
        subjectLabel: 'Meeting title',
        subjectPlaceholder: 'What was the meeting about?',
        contentLabel: 'Meeting notes',
        contentHint: 'Capture key discussion points, decisions, and next steps.',
        extraRecipientsLabel: 'Additional attendee references',
        extraRecipientsPlaceholder: 'name@example.com, attendee note, external participant',
        submitLabel: isEditMode ? 'Save meeting changes' : 'Save meeting record',
      };
    }

    return {
      occurredAtLabel: 'Occurred at',
      subjectLabel: 'Subject',
      subjectPlaceholder: 'Record subject',
      contentLabel: 'Message',
      contentHint: 'Add the communication details you want to keep on record.',
      extraRecipientsLabel: 'Additional recipient references',
      extraRecipientsPlaceholder: 'name@example.com, phone number note, external reference',
      submitLabel: isEditMode ? 'Save changes' : 'Save record',
    };
  }, [isEditMode, isLiveMeeting, isPhoneCall]);

  const canSubmit =
    !!subject.trim() &&
    !!stripHtml(content) &&
    !!(selectedUserIds.length || selectedClientIds.length || recipientReferences.length);
  const normalizedOccurredAt = occurredAt?.second(0).millisecond(0);

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Communication Record' : 'Report External Communication'}
      classes={{ dialogPanel: 'lg:!max-w-5xl' }}
    >
      <div className="grid lg:grid-cols-2 gap-4 lg:gap-0 mt-2">
        <div className="space-y-4 lg:pr-6 lg:border-r">
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Record type"
              options={correspondenceTypeOptions}
              value={correspondenceType}
              onChange={value => value && setCorrespondenceType(value)}
            />
            <CalendarInput
              label={formCopy.occurredAtLabel}
              value={occurredAt}
              onChange={value => setOccurredAt(value)}
              showTime={{ format: 'HH:mm', showSecond: false }}
              format="DD-MM-YYYY HH:mm"
              placeholder="Select date and time"
            />
          </div>
          <TextInput
            name="subject"
            label={formCopy.subjectLabel}
            value={subject}
            onChange={event => setSubject(event.target.value)}
            placeholder={formCopy.subjectPlaceholder}
            required
            disabled={isLoading}
          />
          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              {formCopy.contentLabel}
            </div>
            <p className="text-xs text-gray-500">{formCopy.contentHint}</p>
          </div>
          <ReactQuill
            value={content}
            modules={modules}
            formats={formats}
            theme="snow"
            onChange={setContent}
            readOnly={isLoading}
            className="[&>.ql-container]:min-h-[250px] [&>.ql-container>.ql-editor]:min-h-[250px]"
          />
          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              Attachments
            </div>
            <Dropzone
              multiple
              maxFiles={10}
              value=""
              acceptFormat={[
                'doc',
                'xls',
                'csv',
                'pdf',
                'png',
                'jpeg',
                'bmp',
                'odt',
                'html',
                'ppt',
                'tiff',
                'txt',
                'webp',
                'gif',
                'msg',
              ]}
              loading={isLoading}
              onFileAccepted={handleAcceptFiles}
              onFileClear={() => {
                setSelectedFiles([]);
              }}
            />
            {!!selectedFiles.length && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {selectedFiles.map(file => (
                  <span
                    key={`${file.name}_${file.size}_${file.lastModified}`}
                    className="inline-flex items-center gap-2 rounded bg-gray-100 px-2 py-1"
                  >
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="flex items-center justify-center"
                      onClick={() => handleRemoveSelectedFile(file)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <img src={TrashIcon} alt="remove" className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {fullRecordQuery.isLoading && !!record && !record.attachments && (
            <div className="space-y-2">
              <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
                Existing attachments
              </div>
              <div className="text-xs text-gray-500">Loading attachments...</div>
            </div>
          )}
          {!!existingAttachments.length && (
            <div className="space-y-2">
              <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
                Existing attachments
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {existingAttachments.map(file => (
                  <span
                    key={file.object_key}
                    className="inline-flex items-center gap-2 rounded bg-gray-100 px-2 py-1"
                  >
                    <span>{file.file_name}</span>
                    <button
                      type="button"
                      className="flex items-center justify-center"
                      onClick={() => handleRemoveExistingAttachment(file.object_key)}
                      aria-label={`Remove ${file.file_name}`}
                    >
                      <img src={TrashIcon} alt="remove" className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <Button
            loading={isLoading}
            disabled={!canSubmit || isLoading}
            onClick={async () => {
              const payload = {
                correspondence_type: correspondenceType,
                subject: subject.trim(),
                content: formattedContent,
                client_ids: selectedClientIds,
                user_ids: selectedUserIds,
                recipient_emails: recipientReferences,
                occurred_at: normalizedOccurredAt?.toISOString(),
                optimistic_recipients: additionalRecipientLabels,
                optimistic_recipient_details: additionalRecipientDetails,
                files: selectedFiles,
              };
              onClose();
              if (record) {
                await updateRecord({
                  correspondence_id: resolvedRecord?.id || record.id,
                  optimistic_thread_id:
                    resolvedRecord?.communication_id || resolvedRecord?.id || record.id,
                  optimistic_thread_type: resolvedRecord?.communication_id
                    ? GeneratedCommunicationThreadType.Communication
                    : GeneratedCommunicationThreadType.External,
                  correspondence_type: payload.correspondence_type,
                  subject: payload.subject,
                  content: payload.content,
                  client_ids: payload.client_ids,
                  user_ids: payload.user_ids,
                  recipient_emails: payload.recipient_emails,
                  occurred_at: payload.occurred_at,
                  retained_storage_item_ids: existingAttachments.map(item => item.object_key),
                  files: payload.files,
                });
              } else {
                await createRecord(payload);
              }
            }}
          >
            {formCopy.submitLabel}
          </Button>
        </div>
        <div className="space-y-4 lg:pl-6">
          {isPhoneCall && (
            <TextInput
              name="phone_number"
              label="Phone number"
              value={phoneNumber}
              onChange={event => setPhoneNumber(event.target.value)}
              placeholder="Primary external phone number"
              required
              disabled={isLoading}
            />
          )}
          {isLiveMeeting && (
            <TextInput
              name="meeting_location"
              label="Meeting location"
              value={meetingLocation}
              onChange={event => setMeetingLocation(event.target.value)}
              placeholder="Office, client site, board room, etc."
              disabled={isLoading}
            />
          )}
          <TextInput
            name="recipient_emails"
            label={formCopy.extraRecipientsLabel}
            value={extraEmails}
            onChange={event => setExtraEmails(event.target.value)}
            placeholder={formCopy.extraRecipientsPlaceholder}
            disabled={isLoading}
          />

          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              Clients
            </div>
            <div className="max-h-[220px] relative overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-[2]">
                  <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                    <td className="w-11 pl-3">
                      <CustomCheckbox
                        checked={checkedAllClients}
                        indeterminate={indeterminateClients}
                        className={classNames(
                          '[&_.ant-checkbox-inner]:w-5',
                          '[&_.ant-checkbox-inner]:h-5',
                          '[&_.ant-checkbox-inner]:!rounded-sm',
                          '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                        )}
                        disabled={isLoading || isClientsLoading || !clients.length}
                        onChange={e => handleToggleAllClients(e.target.checked)}
                      />
                    </td>
                    <td>
                      {selectedClientIds.length} {pluralize('Client', selectedClientIds.length)}
                    </td>
                    <td className="pr-3">Email</td>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(item => (
                    <tr key={item.id} className="h-[38px] [&:not(:last-child)]:border-b">
                      <td className="w-11 pl-3">
                        <CustomCheckbox
                          checked={selectedClientIds.includes(item.id)}
                          disabled={isLoading}
                          onChange={e => handleToggleClient(item.id, e.target.checked)}
                          className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                        />
                      </td>
                      <td className="max-w-52 truncate">{item.name}</td>
                      <td className="pr-3 max-w-52 truncate">{item.email || '---'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] leading-[18px] font-medium uppercase text-gray-900 text-opacity-[.64]">
              Users
            </div>
            <div className="max-h-[220px] relative overflow-auto border rounded">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-[2]">
                  <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                    <td className="w-11 pl-3">
                      <CustomCheckbox
                        checked={checkedAllUsers}
                        indeterminate={indeterminateUsers}
                        className={classNames(
                          '[&_.ant-checkbox-inner]:w-5',
                          '[&_.ant-checkbox-inner]:h-5',
                          '[&_.ant-checkbox-inner]:!rounded-sm',
                          '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                        )}
                        disabled={isLoading || isUsersLoading || !users.length}
                        onChange={e => handleToggleAllUsers(e.target.checked)}
                      />
                    </td>
                    <td>
                      {selectedUserIds.length} {pluralize('User', selectedUserIds.length)}
                    </td>
                    <td className="pr-3">Email</td>
                  </tr>
                </thead>
                <tbody>
                  {users.map(item => (
                    <tr key={item.id} className="h-[38px] [&:not(:last-child)]:border-b">
                      <td className="w-11 pl-3">
                        <CustomCheckbox
                          checked={selectedUserIds.includes(item.id)}
                          disabled={isLoading}
                          onChange={e => handleToggleUser(item.id, e.target.checked)}
                          className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                        />
                      </td>
                      <td className="max-w-52 truncate">
                        {[item.first_name, item.last_name].filter(Boolean).join(' ') || '---'}
                      </td>
                      <td className="pr-3 max-w-52 truncate">{item.email || '---'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ModalContentWrapper>
  );
};

export default CreateCommunicationHistoryRecordModal;
